<template>
  <div class="d-flex flex-column flex-lg-row">
    <div v-background-3 class="p-3 w-100 rounded mb-3">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <h5 class="m-0">Gestion des organismes de formations</h5>
        <button class="btn btn-primary" @click="openModal">
          Ajouter un organisme
        </button>
      </div>

      <!-- Ajout du champ de recherche et des options de tri -->
      <div class="d-flex flex-wrap justify-content-between mb-3">
        <div class="input-group mb-2 mr-sm-2" style="max-width: 300px;">
          <div class="input-group-prepend">
            <div class="input-group-text">
              <BaseIcon
                name="search"
                color="#333333"
                width="20"
                height="20"
              ></BaseIcon>
            </div>
          </div>
          <input
            type="text"
            class="form-control"
            placeholder="Rechercher..."
            v-model="searchQuery"
            @input="applySearch"
          />
        </div>

        <div class="d-flex align-items-center">
          <label class="mr-2 mb-0">Trier par :</label>
          <select
            class="form-control form-control-sm mr-2"
            v-model="sortField"
            @change="applySort"
          >
            <option value="description">Description</option>
            <option value="num_cefri">Numéro CEFRI</option>
            <option value="num_scn">Numéro SCN</option>
          </select>

          <button
            class="btn btn-sm"
            :class="sortOrder === 'asc' ? 'btn-primary' : 'btn-outline-primary'"
            @click="toggleSortOrder"
          >
            <i
              :class="
                sortOrder === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
              "
            ></i>
            {{ sortOrder === "asc" ? "ASC" : "DESC" }}
          </button>
        </div>
      </div>

      <div v-if="loading" class="d-flex justify-content-center">
        <Loader />
      </div>
      <div v-else>
        <table class="table table-striped">
          <thead>
            <tr>
              <th @click="setSortField('description')" class="sortable-header">
                Description
                <i
                  v-if="sortField === 'description'"
                  :class="
                    sortOrder === 'asc'
                      ? 'fas fa-sort-up ml-1'
                      : 'fas fa-sort-down ml-1'
                  "
                ></i>
              </th>
              <th>Adresse</th>
              <th @click="setSortField('num_cefri')" class="sortable-header">
                Numéro CEFRI
                <i
                  v-if="sortField === 'num_cefri'"
                  :class="
                    sortOrder === 'asc'
                      ? 'fas fa-sort-up ml-1'
                      : 'fas fa-sort-down ml-1'
                  "
                ></i>
              </th>
              <th @click="setSortField('num_scn')" class="sortable-header">
                Numéro SCN
                <i
                  v-if="sortField === 'num_scn'"
                  :class="
                    sortOrder === 'asc'
                      ? 'fas fa-sort-up ml-1'
                      : 'fas fa-sort-down ml-1'
                  "
                ></i>
              </th>
              <th>Téléphone</th>
              <th>Mail</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="organisme in filteredAndSortedOrganismes"
              :key="organisme.id"
            >
              <td>{{ organisme.description }}</td>
              <td>{{ organisme.adresse }}</td>
              <td>{{ organisme.num_cefri }}</td>
              <td>{{ organisme.num_scn }}</td>
              <td>{{ organisme.telephone }}</td>
              <td>{{ organisme.mail }}</td>
              <td class="text-right">
                <div class="btn-group">
                  <button
                    class="btn btn-sm btn-warning"
                    @click="editOrganisme(organisme)"
                  >
                    Modifier
                  </button>
                  <button
                    class="btn btn-sm btn-danger ml-2"
                    @click="deleteOrganisme(organisme.id)"
                  >
                    Supprimer
                  </button>
                </div>
              </td>
            </tr>
            <!-- Ajout d'une ligne pour indiquer qu'aucun résultat n'a été trouvé -->
            <tr v-if="filteredAndSortedOrganismes.length === 0">
              <td colspan="7" class="text-center">
                <em>Aucun organisme ne correspond à votre recherche</em>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Ajout d'un compteur d'éléments -->
        <div class="text-muted small">
          {{ filteredAndSortedOrganismes.length }} organisme(s) sur
          {{ organismes.length }}
        </div>
      </div>
    </div>

    <!-- Modal pour ajouter/modifier un organisme (inchangé) -->
    <modal
      ref="organismeModal"
      title="Organismes de Formation"
      subtitle="Ajouter ou modifier un organisme"
      id="modal_organisme"
      @save="saveOrganisme"
      @close="closeModal"
      btn=""
      :btns="false"
    >
      <form @submit.prevent="saveOrganisme">
        <!-- Formulaire inchangé -->
        <div class="form-group">
          <label for="description">Description</label>
          <input
            type="text"
            id="description"
            v-model="form.description"
            class="form-control"
          />
        </div>
        <div class="form-group">
          <label for="adresse">Adresse</label>
          <input
            type="text"
            id="adresse"
            v-model="form.adresse"
            class="form-control"
          />
        </div>
        <div class="form-group">
          <label for="num_cefri">Numéro CEFRI</label>
          <input
            type="text"
            id="num_cefri"
            v-model="form.num_cefri"
            class="form-control"
          />
        </div>
        <div class="form-group">
          <label for="num_scn">Numéro SCN</label>
          <input
            type="text"
            id="num_scn"
            v-model="form.num_scn"
            class="form-control"
          />
        </div>
        <div class="form-group">
          <label for="telephone">Téléphone</label>
          <input
            type="text"
            id="telephone"
            v-model="form.telephone"
            class="form-control"
          />
        </div>
        <div class="form-group">
          <label for="mail">Mail</label>
          <input
            type="email"
            id="mail"
            v-model="form.mail"
            class="form-control"
          />
        </div>
        <button type="submit" class="btn btn-primary">Enregistrer</button>
      </form>
    </modal>
  </div>
</template>

<script>
import Loader from "@/components/bases/Loader.vue";
import Modal from "@/components/bases/Modal.vue";
import BaseIcon from "@/components/bases/Icon.vue";

export default {
  components: {
    Loader,
    Modal,
    BaseIcon
  },
  data() {
    return {
      organismes: [],
      loading: false,
      form: {
        id: null,
        description: "",
        adresse: "",
        num_cefri: "",
        num_scn: "",
        telephone: "",
        mail: ""
      },
      // Nouvelles propriétés pour la recherche et le tri
      searchQuery: "",
      sortField: "description", // Par défaut, tri sur le champ description
      sortOrder: "asc", // Par défaut, tri ascendant
      filteredOrganismes: [] // Pour stocker les résultats filtrés
    };
  },
  computed: {
    // Nouvelle propriété calculée pour obtenir les organismes filtrés et triés
    filteredAndSortedOrganismes() {
      return this.filteredOrganismes;
    }
  },
  methods: {
    closeModal() {
      this.resetForm();
      this.$refs.organismeModal.hide();
    },

    // Récupérer les organismes
    fetchOrganismes() {
      this.loading = true;
      this.$http
        .get("/admin/formation_organisme")
        .then(response => {
          this.organismes = response.data;
          this.applySearch(); // Appliquer la recherche et le tri aux données chargées
        })
        .catch(error => console.error(error))
        .finally(() => {
          this.loading = false;
        });
    },

    // Méthode pour filtrer les organismes selon la recherche
    applySearch() {
      const query = this.searchQuery.toLowerCase().trim();

      if (!query) {
        // Si pas de recherche, utiliser tous les organismes
        this.filteredOrganismes = [...this.organismes];
      } else {
        // Filtrer les organismes selon tous les champs
        this.filteredOrganismes = this.organismes.filter(organisme => {
          return (
            (organisme.description &&
              organisme.description.toLowerCase().includes(query)) ||
            (organisme.adresse &&
              organisme.adresse.toLowerCase().includes(query)) ||
            (organisme.num_cefri &&
              organisme.num_cefri.toLowerCase().includes(query)) ||
            (organisme.num_scn &&
              organisme.num_scn.toLowerCase().includes(query)) ||
            (organisme.telephone &&
              organisme.telephone.toLowerCase().includes(query)) ||
            (organisme.mail && organisme.mail.toLowerCase().includes(query))
          );
        });
      }

      // Appliquer le tri après le filtrage
      this.applySort();
    },

    // Méthode pour trier les organismes
    applySort() {
      this.filteredOrganismes.sort((a, b) => {
        // Gérer les valeurs nulles ou undefined
        const valueA = a[this.sortField] || "";
        const valueB = b[this.sortField] || "";

        // Comparer les valeurs selon le type
        if (typeof valueA === "string") {
          // Tri alphabétique pour les chaînes
          return this.sortOrder === "asc"
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        } else {
          // Tri numérique pour les nombres
          return this.sortOrder === "asc" ? valueA - valueB : valueB - valueA;
        }
      });
    },

    // Méthode pour définir le champ de tri
    setSortField(field) {
      // Si on clique sur le même champ, inverser l'ordre
      if (this.sortField === field) {
        this.toggleSortOrder();
      } else {
        // Sinon, définir le nouveau champ et réinitialiser l'ordre à ascendant
        this.sortField = field;
        this.sortOrder = "asc";
      }
      this.applySort();
    },

    // Méthode pour inverser l'ordre de tri
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      this.applySort();
    },

    // Ouvrir le modal pour ajouter un organisme
    openModal() {
      this.resetForm();
      this.$refs.organismeModal.show();
    },

    // Remplir le formulaire pour modifier un organisme
    editOrganisme(organisme) {
      this.form = { ...organisme };
      this.$refs.organismeModal.show();
    },

    // Réinitialiser le formulaire
    resetForm() {
      this.form = {
        id: null,
        description: "",
        adresse: "",
        num_cefri: "",
        num_scn: "",
        telephone: "",
        mail: ""
      };
    },

    // Enregistrer un organisme (ajout ou modification)
    saveOrganisme() {
      const url = this.form.id
        ? `/admin/formation-organisme`
        : `/admin/formation-organisme`;
      const method = this.form.id ? "put" : "post";

      this.$http[method](url, this.form)
        .then(() => {
          this.$refs.organismeModal.hide();
          this.fetchOrganismes();
        })
        .catch(error => console.error(error));
    },

    // Supprimer un organisme
    deleteOrganisme(id) {
      if (
        confirm(
          "Êtes-vous sûr de vouloir supprimer cet organisme de formation ?\n\n ATTENTION : Assurez vous qu'il n'est pas utilisé dans un plan de formation avant de le supprimer."
        )
      ) {
        this.$http
          .delete(`/admin/formation-organisme/${id}`)
          .then(() => {
            this.fetchOrganismes();
          })
          .catch(error => console.error(error));
      }
    }
  },
  mounted() {
    this.fetchOrganismes();
  }
};
</script>

<style scoped>
/* Styles pour les en-têtes de colonnes triables */
.sortable-header {
  cursor: pointer;
  user-select: none;
  position: relative;
}

.sortable-header:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Animation de transition pour le tri */
.table tbody tr {
  transition: all 0.3s ease;
}

/* Style pour l'icône de recherche */
.input-group-text {
  background-color: #f8f9fa;
  border-right: none;
  height: 38px;
}

input.form-control:focus + .input-group-prepend .input-group-text {
  border-color: #80bdff;
}
</style>
